<template>
  <BaseModal ref="modal" modal-classes="max-w-md mx-auto" @modal-after-leave="$emit('closed')">
    <template #header>
      <span class="block text-xs text-gray-700 uppercase tracking-wider">καναλι</span>
      <h3 class="text-2xl text-gray-900">Τροποποίηση</h3>
    </template>

    <template v-if="loading" #default>
      <div class="text-center">
        <Loader />
      </div>
    </template>

    <template v-else #default="modalProps">
      <ValidationObserver slim v-slot="{ handleSubmit, invalid }" ref="observer">
        <form @submit.prevent="handleSubmit(doSubmit)">
          <BaseLayoutSpaced relaxed>
            <BaseLayoutSpaced>
              <ValidationProvider
                v-for="field in formFieldsWithDefaults"
                :key="field.name"
                :name="field.prettyName"
                :vid="field.name"
                :rules="field.validation"
                v-slot="{ errors, failed }"
                slim
              >
                <BaseFormGroup>
                  <BaseFormLabel :for="`${_uid}-${field.name}`">
                    {{ field.label }} <template v-if="field.validation.required"> * </template>
                  </BaseFormLabel>
                  <Component
                    v-model="data[field.name]"
                    :is="field.component"
                    :disabled="submitting"
                    v-bind="{
                      ...field.options.props,
                      ...field.options.attrs,
                    }"
                    :invalid="failed"
                    :id="`${_uid}-${field.name}`"
                  />
                  <BaseFormInlineMessage v-if="failed" danger>
                    {{ errors[0] }}
                  </BaseFormInlineMessage>
                </BaseFormGroup>
              </ValidationProvider>
            </BaseLayoutSpaced>
            <div class="flex space-x-2 py-1 items-baseline">
              <BaseButton primary type="submit" :disabled="invalid || submitting">
                <span v-if="!submitting">Submit</span>
                <Loader v-else small inverse />
              </BaseButton>
              <BaseButton ghost type="button" @click="modalProps.close" :disabled="submitting">
                Close
              </BaseButton>
              <BaseFormInlineMessage v-if="message" danger>
                {{ message }}
              </BaseFormInlineMessage>
            </div>
          </BaseLayoutSpaced>
        </form>
      </ValidationObserver>
    </template>
  </BaseModal>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { HTTP_UNPROCESSABLE_ENTITY } from '@/api/http-response-codes';
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';

const defaultField = {
  component: null,
  label: '',
  name: '',
  options: {},
  validation: {},
};

const defaultData = {
  channableType: null,
  channableId: null,
  pageUuid: null,
};

export default {
  mixins: [veeValidateErrorsMixin],

  props: {
    channel: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    loading: false,
    submitting: false,
    data: null,
    message: null,
  }),

  computed: {
    ...mapGetters('ui/projectPages', ['pages']),

    formFields() {
      return [
        {
          component: 'BaseFormSelect',
          label: 'Channel Type',
          name: 'channableType',
          prettyName: 'channel type',
          options: {
            attrs: {
              name: 'channableType',
            },
            props: {
              options: [{ value: 'facebook-campaigns', label: 'Facebook' }],
            },
          },
          validation: {
            required: true,
            oneOf: ['facebook-campaigns'],
          },
        },
        {
          component: 'BaseFormInput',
          label: 'Campaign Id',
          name: 'channableId',
          prettyName: 'campaign id',
          options: {
            attrs: {
              name: 'channableId',
            },
          },
          validation: {
            required: false,
          },
        },
        {
          component: 'BaseFormSelect',
          label: 'Page',
          name: 'pageUuid',
          prettyName: 'page',
          options: {
            attrs: {
              name: 'pageUuid',
            },
            props: {
              options: this.pages.map((page) => ({ value: page.id, label: page.title })),
            },
          },
          validation: {
            required: false,
            oneOf: this.pages.map((page) => page.id),
          },
        },
      ];
    },
    formFieldsWithDefaults() {
      return this.formFields.map((x) => ({ ...defaultField, ...x }));
    },
  },

  created() {
    this.initializeForm();
    this.data = { ...defaultData, ...this.channel };
  },

  destroyed() {
    this.setPageIds([]);
  },

  methods: {
    ...mapActions('ui/channels', {
      updateChannel: 'update',
    }),

    ...mapActions('ui/projectPages', {
      loadPages: 'load',
    }),

    ...mapMutations('ui/projectPages', {
      setPageIds: 'setIds',
    }),

    async initializeForm() {
      this.loading = true;
      try {
        await this.loadPages({ projectUuid: this.channel.projectUuid });
      } catch (e) {
        //
      }
      this.loading = false;
    },

    async doSubmit() {
      const submitWithErrorHandling = this.handleFormErrors(this.updateChannel);

      this.submitting = true;

      await submitWithErrorHandling({ channelId: this.channel.id, formData: this.data });

      this.submitting = false;
    },

    handleFormErrors(fn) {
      return (...params) =>
        fn(...params)
          .then(() => {
            this.$refs.modal.doClose();
          })
          .catch(({ status, data } = {}) => {
            // do sth with the error!
            if (status && status === HTTP_UNPROCESSABLE_ENTITY) {
              this.observer = this.$refs.observer;
              this.addServerErrors(data.errors);
            }
          });
    },
  },
};
</script>
